import React, { useState } from 'react';
import MediaForm from './MediaForm';
import { Paper, Grid, withStyles } from '@material-ui/core';
import History from '../../../History';
import * as actions from '../store/mediaActions';
import { BackButton, hasPermission, parseURL } from '../../sc-utilities';
import { connect } from 'react-redux';
import convert from 'heic-convert/browser';
import config from '../../../config/config';
import imageCompression from 'browser-image-compression';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        paddingRight: theme.spacing(6),
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            paddingRight: 0,
        },
    },
});

const file2Buffer = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const readFile = (event) => {
            const buffer = reader.result;
            resolve(buffer);
        };

        reader.addEventListener('load', readFile);
        reader.readAsArrayBuffer(file);
    });
};

const getImageResolution = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const readFile = (event) => {
            const image = new Image();

            image.src = event.target.result;

            image.onload = function () {
                resolve({ width: this.width, height: this.height });
            };
        };

        reader.addEventListener('load', readFile);
        reader.readAsDataURL(file);
    });
};

const resizeAndCompressFile = async (file) => {
    if (config?.upload?.media?.compressImages || config?.upload?.media?.resizeImages) {
        try {
            const desiredWidth = config?.upload?.media?.resizeToWidth ?? 2160;
            const options = {
                useWebWorker: true,
            };

            const { width, height } = await getImageResolution(file);
            if (
                config?.upload?.media?.resizeImages &&
                (width > (desiredWidth ?? width) || height > (config?.upload?.media?.resizeToHeight ?? height))
            ) {
                if (width >= height) {
                    options.maxWidthOrHeight = desiredWidth;
                } else {
                    options.maxWidthOrHeight = (height / width) * desiredWidth;
                }
            }
            if (config?.upload?.media?.compressImages) {
                options.initialQuality = config?.upload?.media?.compressionQuality / 100;
            }
            return new File([await imageCompression(file, options)], file.name, {
                type: 'image/jpeg',
                lastModified: file.lastModified,
            });
        } catch (error) {
            return file;
        }
    }
    return file;
};

const MediaUpload = (props) => {
    const { classes, activeCustomer, translations, permissions, onUploadMedia } = props;
    const [filesToSend, setFilesToSend] = useState({ files: [] });

    let searchParams = parseURL(window.location.search);

    const handleRemove = () => {
        setFilesToSend({ files: [] });
    };

    const handleUpload = async (event) => {
        let files = event;
        if (Array.isArray(files) && files.length === 0) {
            setFilesToSend({ files: [] });
            return;
        }
        let processedFiles = [];
        if (Array.isArray(files)) {
            await Promise.all(
                files.map(async (file) => {
                    const pos = file.name.lastIndexOf('.');
                    const extensionName = file.name.substring(pos, file.name.length).toLowerCase();
                    const fileOptions = {
                        type: 'image/jpeg',
                        lastModified: file.lastModified,
                    };
                    let fileName = `${file.name.substring(0, pos)}.jpg`;
                    if (
                        config?.upload?.media?.convertHeicImages &&
                        ['.heic', '.heics', '.heif', '.heifs'].includes(extensionName)
                    ) {
                        let buffer = await file2Buffer(file);
                        if (!config?.upload?.media?.convertOnlyMainImageFromHeic) {
                            const images = await convert.all({
                                buffer: new Uint8Array(buffer),
                                format: 'JPEG',
                            });

                            if (images?.length === 1) {
                                const image = images[0];
                                buffer = await image.convert();
                                const processedFile = await resizeAndCompressFile(
                                    new File([buffer.buffer], fileName, fileOptions)
                                );
                                processedFiles.push(processedFile);
                            } else if (images?.length > 1) {
                                for (let idx in images) {
                                    const image = images[idx];
                                    fileName = `${file.name.substring(0, pos)}-${idx}.jpg`;
                                    buffer = await image.convert();
                                    const processedFile = await resizeAndCompressFile(
                                        new File([buffer.buffer], fileName, fileOptions)
                                    );
                                    processedFiles.push(processedFile);
                                }
                            }
                        } else {
                            buffer = await convert({
                                buffer: new Uint8Array(buffer),
                                format: 'JPEG',
                            });
                            const processedFile = await resizeAndCompressFile(
                                new File([buffer.buffer], fileName, fileOptions)
                            );
                            processedFiles.push(processedFile);
                        }
                    } else {
                        const processedFile = await resizeAndCompressFile(file);
                        processedFiles.push(processedFile);
                    }
                })
            );
        }
        setFilesToSend({ files: [...filesToSend.files, ...processedFiles] });
    };

    const handleSubmit = async () => {
        const data = new FormData();
        for (const [i, file] of filesToSend.files.entries()) {
            data.append(`files[${i}]`, file);
        }
        data.append('ActiveCustomerID', activeCustomer);

        let token = localStorage.getItem('SESSION');

        data.append('Token', token);
        if (searchParams.productID) {
            data.append('Type', 'Product');
            data.append('ProductID', searchParams.productID);
        } else if (searchParams.orderID) {
            data.append('Type', 'Order');
            data.append('OrderID', searchParams.orderID);
        } else if (searchParams.transportColliID) {
            data.append('Type', 'TransportColli');
            data.append('TransportColliID', searchParams.transportColliID);
        }
        let result = await onUploadMedia(data);
        if (result) {
            History.goBack();
        }
    };

    let initialValues = {};

    let formSettings = {
        FormTitle: translations['upload_photos'] || 'Upload photos',
        ButtonSubmit: hasPermission(permissions, 'media_upload')
            ? {
                  Title: translations['save'] || 'SAVE',
                  Color: 'primary',
              }
            : null,
    };

    return (
        <Paper className={classes.root}>
            <Grid container spacing={10}>
                <Grid item xs={12}>
                    {BackButton(translations)}
                    <MediaForm
                        formSettings={formSettings}
                        initialValues={initialValues}
                        handleSubmit={handleSubmit}
                        loading={filesToSend.files.length === 0}
                        handleUpload={handleUpload}
                        handleRemove={handleRemove}
                        translations={translations}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        permissions: state?.core?.login?.permissions || [],
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onUploadMedia: (data) => dispatch(actions.uploadMedia(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(MediaUpload));
